import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../services/api';

const PAGE_SIZE = 25;

const formatDateTime = (iso) => {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
};

const AdminVouchersPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState('');
  const [createdCode, setCreatedCode] = useState('');
  const [formValues, setFormValues] = useState({
    code: '',
    label: '',
    tokenAmount: '100',
    maxRedemptions: '1',
    expiresAt: '',
    notes: '',
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-vouchers', { page, search, active }],
    queryFn: async () => {
      const params = { page, page_size: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (active !== '') params.active = active === 'active';
      const response = await adminAPI.getVouchers(params);
      return response.data;
    },
    keepPreviousData: true,
  });

  const createVoucherMutation = useMutation({
    mutationFn: (payload) => adminAPI.createVoucher(payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['admin-vouchers']);
      setCreatedCode(response?.data?.code || '');
      setFormValues({
        code: '',
        label: '',
        tokenAmount: '100',
        maxRedemptions: '1',
        expiresAt: '',
        notes: '',
      });
    },
  });

  const updateVoucherMutation = useMutation({
    mutationFn: ({ id, payload }) => adminAPI.updateVoucher(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-vouchers']);
    },
  });

  const vouchers = data?.items || [];
  const pagination = data?.pagination || {
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };
  const summary = data?.summary || { total: 0, redemptions: 0, tokensGranted: 0 };
  const recentRedemptions = data?.recentRedemptions || [];

  const canGoPrev = useMemo(() => pagination.page > 1, [pagination.page]);
  const canGoNext = useMemo(
    () => pagination.totalPages && pagination.page < pagination.totalPages,
    [pagination.page, pagination.totalPages]
  );

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateVoucher = (event) => {
    event.preventDefault();
    const tokenAmount = Number(formValues.tokenAmount);
    const maxRedemptions = Number(formValues.maxRedemptions);

    if (!Number.isFinite(tokenAmount) || tokenAmount <= 0) {
      window.alert('Token amount must be greater than zero.');
      return;
    }
    if (!Number.isFinite(maxRedemptions) || maxRedemptions <= 0) {
      window.alert('Max redemptions must be greater than zero.');
      return;
    }

    createVoucherMutation.mutate({
      code: formValues.code.trim() || undefined,
      label: formValues.label.trim() || undefined,
      tokenAmount: Math.trunc(tokenAmount),
      maxRedemptions: Math.trunc(maxRedemptions),
      expiresAt: formValues.expiresAt
        ? new Date(formValues.expiresAt).toISOString()
        : undefined,
      notes: formValues.notes.trim() || undefined,
    });
  };

  const handleToggleActive = (voucher) => {
    updateVoucherMutation.mutate({
      id: voucher.id,
      payload: { isActive: !voucher.isActive },
    });
  };

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Vouchers
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create token pack vouchers and monitor redemptions.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Vouchers
          </p>
          <p className="mt-1 text-2xl font-semibold">{summary.total}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Redemptions
          </p>
          <p className="mt-1 text-2xl font-semibold">{summary.redemptions}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Tokens granted
          </p>
          <p className="mt-1 text-2xl font-semibold">
            {Number(summary.tokensGranted || 0).toLocaleString()}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleCreateVoucher}
        className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Create voucher</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Leave code blank to generate a one-time code. The full code is shown once.
            </p>
          </div>
          <button
            type="submit"
            disabled={createVoucherMutation.isLoading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {createVoucherMutation.isLoading ? 'Creating...' : 'Create'}
          </button>
        </div>

        {createdCode && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
            New voucher code:{' '}
            <span className="font-mono font-semibold tracking-wide">{createdCode}</span>
          </div>
        )}

        {createVoucherMutation.isError && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {createVoucherMutation.error?.response?.data?.detail ||
              'Failed to create voucher.'}
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-6">
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Code
            </label>
            <input
              type="text"
              value={formValues.code}
              onChange={(event) => handleFormChange('code', event.target.value)}
              placeholder="Optional custom code"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Label
            </label>
            <input
              type="text"
              value={formValues.label}
              onChange={(event) => handleFormChange('label', event.target.value)}
              placeholder="Campaign or recipient"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tokens
            </label>
            <input
              type="number"
              min="1"
              value={formValues.tokenAmount}
              onChange={(event) => handleFormChange('tokenAmount', event.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Uses
            </label>
            <input
              type="number"
              min="1"
              value={formValues.maxRedemptions}
              onChange={(event) => handleFormChange('maxRedemptions', event.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Expires
            </label>
            <input
              type="datetime-local"
              value={formValues.expiresAt}
              onChange={(event) => handleFormChange('expiresAt', event.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
          <div className="md:col-span-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Notes
            </label>
            <input
              type="text"
              value={formValues.notes}
              onChange={(event) => handleFormChange('notes', event.target.value)}
              placeholder="Internal context"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
            />
          </div>
        </div>
      </form>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Search
          </label>
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Filter by label, notes, or exact code"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Status
          </label>
          <select
            value={active}
            onChange={(event) => {
              setActive(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="flex items-end text-sm text-gray-500 dark:text-gray-400">
          Total rows: <span className="ml-1 font-medium">{pagination.total}</span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-500">
          Loading vouchers...
        </div>
      ) : isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load vouchers.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Created</th>
                  <th className="px-3 py-2 text-left font-medium">Code</th>
                  <th className="px-3 py-2 text-left font-medium">Label</th>
                  <th className="px-3 py-2 text-right font-medium">Tokens</th>
                  <th className="px-3 py-2 text-right font-medium">Used</th>
                  <th className="px-3 py-2 text-left font-medium">Expires</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {vouchers.map((voucher) => (
                  <tr key={voucher.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {formatDateTime(voucher.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                      {voucher.codePreview}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{voucher.label || 'Unlabeled'}</div>
                      {voucher.notes && (
                        <div className="max-w-xs truncate text-xs text-gray-500">
                          {voucher.notes}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium">
                      {voucher.tokenAmount.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {voucher.redemptionCount} / {voucher.maxRedemptions}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {formatDateTime(voucher.expiresAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          voucher.isActive
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {voucher.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(voucher)}
                        disabled={updateVoucherMutation.isLoading}
                        className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
                      >
                        {voucher.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}

                {vouchers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-500">
                      No vouchers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages || 1}
            </div>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => canGoPrev && setPage((current) => Math.max(1, current - 1))}
                disabled={!canGoPrev}
                className="rounded border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => canGoNext && setPage((current) => current + 1)}
                disabled={!canGoNext}
                className="rounded border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h2 className="text-base font-semibold">Recent redemptions</h2>
        <div className="mt-3 divide-y divide-gray-100 dark:divide-gray-800">
          {recentRedemptions.map((redemption) => (
            <div key={redemption.id} className="flex items-center justify-between gap-4 py-3 text-sm">
              <div>
                <div className="font-medium">
                  {redemption.user?.username || 'Unknown user'} redeemed{' '}
                  {redemption.voucher?.label || redemption.voucher?.codePreview}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {redemption.user?.email} · {formatDateTime(redemption.redeemedAt)}
                </div>
              </div>
              <div className="whitespace-nowrap font-medium">
                +{redemption.ledger?.amount || redemption.voucher?.tokenAmount || 0} tokens
              </div>
            </div>
          ))}

          {recentRedemptions.length === 0 && (
            <div className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No voucher redemptions yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminVouchersPage;
